import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Save, X, Loader2, ChevronUp, ChevronDown, Eye, EyeOff, Globe } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import SeoPanel from '../../components/SeoPanel';
import { analyseSeo } from '../../utils/seoAnalysis';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

const EMPTY = {
  title: '', slug: '', hero_heading: '', hero_subheading: '',
  content: '', cta_heading: '', cta_url: '', cta_text: 'Get Started',
  meta_title: '', meta_description: '',
  // Same SEO field set as the blog, so the editor is identical wherever you are.
  focus_keyword: '', secondary_keywords: '', canonical_url: '',
  robots_index: true, robots_follow: true,
  og_title: '', og_description: '', og_image: '',
  twitter_title: '', twitter_description: '', twitter_image: '',
  schema_type: 'WebPage', breadcrumb_title: '', is_pillar: false,
  is_active: true, sort_order: 0,
};

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const inputCls = 'w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30';
const labelCls = 'block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1';

function PageModal({ page, onClose, onSave, saving }) {
  const [form, setForm] = useState(page
    ? {
        ...EMPTY,
        ...page,
        secondary_keywords: Array.isArray(page.secondary_keywords)
          ? page.secondary_keywords.join(', ') : (page.secondary_keywords || ''),
        // NULL from a row predating these columns must read as "indexable", not "noindex".
        robots_index: page.robots_index !== false,
        robots_follow: page.robots_follow !== false,
        schema_type: page.schema_type || 'WebPage',
      }
    : { ...EMPTY });
  const [autoSlug, setAutoSlug] = useState(!page);

  function setField(key) {
    return e => {
      const val = e.target.value;
      setForm(f => {
        const next = { ...f, [key]: val };
        if (key === 'title' && autoSlug) next.slug = slugify(val);
        return next;
      });
    };
  }

  function setCheck(key) { return e => setForm(f => ({ ...f, [key]: e.target.checked })); }
  function setNum(key) { return e => setForm(f => ({ ...f, [key]: parseInt(e.target.value) || 0 })); }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">{page ? 'Edit Page' : 'New Page'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Title & slug */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Page Title</label>
              <input type="text" value={form.title} onChange={setField('title')} className={inputCls} placeholder="About Our Services" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>
                URL Slug
                <span className="ml-2 text-slate-400 font-normal normal-case">
                  — site.com/<strong>{form.slug || 'my-page'}</strong>
                </span>
              </label>
              <input type="text" value={form.slug} onChange={e => { setAutoSlug(false); setField('slug')(e); }}
                className={`${inputCls} font-mono`} placeholder="about-our-services" />
              <p className="text-xs text-slate-400 mt-1">Lowercase letters, numbers and hyphens only. Avoid existing routes: about, blog, features, pricing, testimonials, contact.</p>
            </div>
          </div>

          {/* Hero */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Hero Section (optional)</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Hero Heading</label>
                <input type="text" value={form.hero_heading || ''} onChange={setField('hero_heading')} className={inputCls} placeholder="Your page headline" />
              </div>
              <div>
                <label className={labelCls}>Hero Subheading</label>
                <input type="text" value={form.hero_subheading || ''} onChange={setField('hero_subheading')} className={inputCls} placeholder="A brief description" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-2 border-t border-slate-100">
            <label className={`${labelCls} mb-2`}>Page Content</label>
            <RichTextEditor
              value={form.content || ''}
              onChange={html => setForm(f => ({ ...f, content: html }))}
              placeholder="Start writing your page content, or click 'Write with AI' to generate content automatically..."
              minHeight={280}
            />
          </div>

          {/* CTA */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Bottom CTA Banner (optional)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3">
                <label className={labelCls}>CTA Heading</label>
                <input type="text" value={form.cta_heading || ''} onChange={setField('cta_heading')} className={inputCls} placeholder="Ready to get started?" />
              </div>
              <div>
                <label className={labelCls}>Button Text</label>
                <input type="text" value={form.cta_text || ''} onChange={setField('cta_text')} className={inputCls} placeholder="Get Started" />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Button URL</label>
                <input type="text" value={form.cta_url || ''} onChange={setField('cta_url')} className={inputCls} placeholder="https://app.rostery.com.au/register" />
              </div>
            </div>
          </div>

          {/* SEO — the full panel, same component the blog editor uses. */}
          <div className="pt-2 border-t border-slate-100">
            <SeoPanel
              kind="page"
              urlPrefix="/"
              title={form.title}
              content={form.content}
              excerpt={form.hero_subheading}
              value={form}
              onChange={next => setForm(next)}
            />
          </div>

          {/* Options */}
          <div className="pt-2 border-t border-slate-100 flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={setCheck('is_active')}
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
              <span className="text-sm text-slate-700 font-medium">Active (visible on website)</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 font-medium">Sort order</label>
              <input type="number" value={form.sort_order} onChange={setNum('sort_order')}
                className="w-20 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.title || !form.slug}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            Save Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Pages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | page object
  const token = localStorage.getItem('token');

  async function fetchPages() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/pages/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPages(Array.isArray(data) ? data.sort((a, b) => a.sort_order - b.sort_order) : []);
    } catch { toast.error('Failed to load pages'); }
    finally { setLoading(false); }
  }

  useEffect(() => { fetchPages(); }, []);

  async function savePage(rawForm) {
    setSaving(true);
    try {
      // The panel edits secondary keywords as a comma string; the API stores an array.
      // Score is computed here and stored so a list view can find weak pages later.
      const secondary = String(rawForm.secondary_keywords || '')
        .split(',').map(t => t.trim()).filter(Boolean);
      const form = {
        ...rawForm,
        secondary_keywords: secondary,
        seo_score: analyseSeo({
          title: rawForm.title,
          metaTitle: rawForm.meta_title,
          metaDescription: rawForm.meta_description,
          slug: rawForm.slug,
          content: rawForm.content,
          excerpt: rawForm.hero_subheading,
          focusKeyword: rawForm.focus_keyword,
          secondaryKeywords: secondary,
        }).score,
      };
      const isNew = !form.id;
      const res = await fetch(
        isNew ? `${API}/api/marketing/pages` : `${API}/api/marketing/pages/${form.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Save failed');
      }
      toast.success(isNew ? 'Page created' : 'Page updated');
      setModal(null);
      fetchPages();
    } catch (e) { toast.error(e.message || 'Failed to save page'); }
    finally { setSaving(false); }
  }

  async function deletePage(id) {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/marketing/pages/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Page deleted');
      fetchPages();
    } catch { toast.error('Failed to delete page'); }
  }

  async function toggleActive(page) {
    try {
      await fetch(`${API}/api/marketing/pages/${page.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...page, is_active: !page.is_active }),
      });
      fetchPages();
    } catch { toast.error('Failed to update page'); }
  }

  async function move(id, dir) {
    const idx = pages.findIndex(p => p.id === id);
    const target = pages[idx + dir];
    if (!target) return;
    await Promise.all([
      fetch(`${API}/api/marketing/pages/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...pages[idx], sort_order: target.sort_order }),
      }),
      fetch(`${API}/api/marketing/pages/${target.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...target, sort_order: pages[idx].sort_order }),
      }),
    ]);
    fetchPages();
  }

  const siteUrl = MARKETING_URL;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pages</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create and manage custom pages on the marketing website</p>
        </div>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors">
          <Plus size={16} /> New Page
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-20 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
          <p className="text-lg font-medium">No pages yet</p>
          <p className="text-sm mt-1">Click "New Page" to create your first custom page</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map((page, idx) => (
            <div key={page.id}
              className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
              {/* Reorder */}
              <div className="flex flex-col gap-1">
                <button onClick={() => move(page.id, -1)} disabled={idx === 0}
                  className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronUp size={14} />
                </button>
                <button onClick={() => move(page.id, 1)} disabled={idx === pages.length - 1}
                  className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30">
                  <ChevronDown size={14} />
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-slate-900">{page.title}</span>
                  {!page.is_active && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Globe size={11} />
                  <span className="font-mono">{siteUrl}/{page.slug}</span>
                </div>
                {page.hero_heading && (
                  <p className="text-xs text-slate-400 mt-0.5 truncate max-w-lg">{page.hero_heading}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <a href={`${siteUrl}/${page.slug}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50">
                  <Globe size={12} /> Preview
                </a>
                <button onClick={() => toggleActive(page)}
                  className={`p-2 rounded-lg border transition-colors ${page.is_active
                    ? 'text-green-600 border-green-200 hover:bg-green-50'
                    : 'text-slate-400 border-slate-200 hover:bg-slate-50'}`}
                  title={page.is_active ? 'Deactivate' : 'Activate'}>
                  {page.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={() => setModal(page)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                  <Edit2 size={13} /> Edit
                </button>
                <button onClick={() => deletePage(page.id)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-500 border border-red-200 rounded-lg hover:bg-red-50">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <PageModal
          page={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={savePage}
          saving={saving}
        />
      )}
    </div>
  );
}
